import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  forwardRef,
  inject,
  signal,
  viewChild,
  ChangeDetectorRef,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { EditorState, Compartment } from '@codemirror/state';
import { EditorView, keymap, lineNumbers, placeholder } from '@codemirror/view';
import { defaultKeymap, history, historyKeymap } from '@codemirror/commands';
import { markdown } from '@codemirror/lang-markdown';
import { syntaxHighlighting, HighlightStyle } from '@codemirror/language';
import { tags } from '@lezer/highlight';
import { MarkdownModule } from 'ngx-markdown';
import { MaterialModule } from '../../material.module';

@Component({
  selector: 'app-markdown-editor',
  templateUrl: './markdown-editor.component.html',
  styleUrl: './markdown-editor.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MarkdownModule, MaterialModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MarkdownEditorComponent),
      multi: true,
    },
  ],
})
export class MarkdownEditorComponent implements ControlValueAccessor, AfterViewInit, OnDestroy {
  readonly editorHost = viewChild.required<ElementRef>('editorHost');
  readonly isDisabled = signal(false);

  showPreview = signal(false);
  content = signal('');

  private cdr = inject(ChangeDetectorRef);
  private editorView: EditorView | null = null;
  private editableCompartment = new Compartment();
  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  ngAfterViewInit(): void {
    this.initEditor();
  }

  ngOnDestroy(): void {
    this.editorView?.destroy();
  }

  private editorTheme = EditorView.theme({
    '&': {
      backgroundColor: '#ffffff',
      color: '#24292f',
      fontSize: '14px',
    },
    '&.cm-focused': {
      outline: '2px solid #ff7900',
      outlineOffset: '-1px',
    },
    '.cm-content': {
      caretColor: '#24292f',
      padding: '8px 0',
      fontFamily: "'Manrope', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      lineHeight: '1.6',
    },
    '.cm-line': {
      padding: '0 12px',
    },
    '.cm-gutters': {
      backgroundColor: '#f6f8fa',
      color: '#8b949e',
      borderRight: '1px solid #d0d7de',
    },
    '.cm-activeLineGutter': {
      backgroundColor: '#eaeef2',
      color: '#24292f',
    },
    '.cm-activeLine': {
      backgroundColor: '#f6f8fa',
    },
    '.cm-selectionBackground': {
      backgroundColor: '#b6d7ff !important',
    },
    '.cm-cursor': {
      borderLeftColor: '#24292f',
    },
    '.cm-placeholder': {
      color: '#8b949e',
      fontStyle: 'italic',
    },
  });

  private markdownHighlightStyle = syntaxHighlighting(
    HighlightStyle.define([
      { tag: tags.heading1, fontWeight: '700', fontSize: '1.5em', color: '#24292f' },
      { tag: tags.heading2, fontWeight: '700', fontSize: '1.3em', color: '#24292f' },
      { tag: tags.heading3, fontWeight: '600', fontSize: '1.15em', color: '#24292f' },
      { tag: tags.heading4, fontWeight: '600', fontSize: '1em', color: '#24292f' },
      { tag: tags.strong, fontWeight: '700', color: '#24292f' },
      { tag: tags.emphasis, fontStyle: 'italic', color: '#24292f' },
      { tag: tags.link, color: '#0969da', textDecoration: 'underline' },
      { tag: tags.url, color: '#6e7781' },
      { tag: tags.monospace, fontFamily: "'SFMono-Regular', Consolas, monospace", backgroundColor: '#f0f3f6', borderRadius: '3px' },
      { tag: tags.processingInstruction, color: '#8b949e' },
      { tag: tags.quote, color: '#57606a', fontStyle: 'italic' },
      { tag: tags.strikethrough, textDecoration: 'line-through', color: '#6e7781' },
      { tag: tags.contentSeparator, color: '#d0d7de' },
    ])
  );

  private initEditor(): void {
    const startState = EditorState.create({
      doc: this.content(),
      extensions: [
        this.editorTheme,
        this.markdownHighlightStyle,
        lineNumbers(),
        history(),
        keymap.of([...defaultKeymap, ...historyKeymap]),
        markdown(),
        placeholder('Write markdown here...'),
        this.editableCompartment.of(EditorView.editable.of(true)),
        EditorView.updateListener.of((update) => {
          if (update.docChanged) {
            const value = update.state.doc.toString();
            this.content.set(value);
            this.onChange(value);
          }
        }),
        EditorView.domEventHandlers({
          focusout: () => {
            this.onTouched();
          },
        }),
        EditorView.lineWrapping,
      ],
    });

    this.editorView = new EditorView({
      state: startState,
      parent: this.editorHost().nativeElement,
    });
  }

  writeValue(value: string): void {
    this.content.set(value || '');
    if (this.editorView) {
      const currentValue = this.editorView.state.doc.toString();
      if (currentValue !== (value || '')) {
        this.editorView.dispatch({
          changes: { from: 0, to: currentValue.length, insert: value || '' },
        });
      }
    }
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled.set(isDisabled);
    if (this.editorView) {
      this.editorView.dispatch({
        effects: this.editableCompartment.reconfigure(EditorView.editable.of(!isDisabled)),
      });
    }
    this.cdr.markForCheck();
  }

  togglePreview(): void {
    this.showPreview.update((v) => !v);
  }

  insertSyntax(before: string, after: string): void {
    if (!this.editorView) return;
    const { from, to } = this.editorView.state.selection.main;
    const selected = this.editorView.state.sliceDoc(from, to);
    const replacement = before + selected + after;
    this.editorView.dispatch({
      changes: { from, to, insert: replacement },
      selection: { anchor: from + before.length, head: from + before.length + selected.length },
    });
    this.editorView.focus();
  }

  insertAtLineStart(prefix: string): void {
    if (!this.editorView) return;
    const { from } = this.editorView.state.selection.main;
    const line = this.editorView.state.doc.lineAt(from);
    this.editorView.dispatch({
      changes: { from: line.from, to: line.from, insert: prefix },
    });
    this.editorView.focus();
  }

  onBold(): void {
    this.insertSyntax('**', '**');
  }

  onItalic(): void {
    this.insertSyntax('*', '*');
  }

  onHeading(level: number): void {
    this.insertAtLineStart('#'.repeat(level) + ' ');
  }

  onLink(): void {
    this.insertSyntax('[', '](url)');
  }

  onImage(): void {
    this.insertSyntax('![', '](url)');
  }

  onBulletList(): void {
    this.insertAtLineStart('- ');
  }

  onNumberedList(): void {
    this.insertAtLineStart('1. ');
  }

  onCodeBlock(): void {
    this.insertSyntax('```\n', '\n```');
  }
}
